import { Group, Text, type TextProps } from '@mantine/core';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyButton } from './CopyButton';

export function CopyableText({
    children,
    copyValue,
    label,
    monospace,
    ...textProps
}: {
    children: ReactNode;
    /** Value written to clipboard; defaults to children when it is a string */
    copyValue?: string;
    label?: string;
    monospace?: boolean;
} & TextProps) {
    const resolvedCopy =
        copyValue ?? (typeof children === 'string' ? children : undefined);

    if (!resolvedCopy?.trim()) {
        return (
            <Text {...textProps} ff={monospace ? 'monospace' : textProps.ff}>
                {children}
            </Text>
        );
    }

    return (
        <Group gap={4} wrap="nowrap" align="center">
            <Text
                {...textProps}
                ff={monospace ? 'monospace' : textProps.ff}
                style={{ flex: 1, minWidth: 0, ...textProps.style }}
            >
                {children}
            </Text>
            <CopyButton value={resolvedCopy} label={label} />
        </Group>
    );
}

/** Full UUID (or id) with a one-click copy control. */
export function CopyableId({
    id,
    display,
    size = 'sm',
}: {
    id: string;
    display?: string;
    size?: TextProps['size'];
}) {
    const { t } = useTranslation();

    return (
        <CopyableText copyValue={id} label={t('common.copy.id')} size={size} ff="monospace" c="dimmed">
            {display ?? id}
        </CopyableText>
    );
}
